import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout1 from "../../Layouts/Layout1";
import LayoutContainer from "../../Layouts/LayoutContainer";
import { ChevronLeft, HelpCircle } from "lucide-react";
import { CustomSelect } from "../CustomSelection";
import RentalDataUseModal from "./RentalDataUseModal";
import { useRentalProfile } from "../../context/RentalProfileContext";
import { RENTAL_PROFILE_SECTIONS } from "../../Constants/rentalProfile";

const MONTHS = [
  { value: "", label: "Month" },
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const buildYears = () => {
  const y = new Date().getFullYear();
  const opts = [{ value: "", label: "Year" }];
  for (let yr = y; yr >= y - 100; yr -= 1) {
    opts.push({ value: String(yr), label: String(yr) });
  }
  return opts;
};

const buildDays = () => {
  const opts = [{ value: "", label: "Day" }];
  for (let d = 1; d <= 31; d += 1) {
    opts.push({ value: String(d), label: String(d) });
  }
  return opts;
};

const inputClass =
  "w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#1A1A1A] outline-none transition-shadow placeholder:text-[#9CA3AF] focus:border-[#097FE6] focus:ring-2 focus:ring-[#097FE6]/20";

const PersonalDetails = () => {
  const navigate = useNavigate();
  const { profile, loading, saving, updateSection } = useRentalProfile();
  const [dataUseModalOpen, setDataUseModalOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [phone, setPhone] = useState("");

  const profileEmail = useMemo(
    () =>
      profile.personalDetails.email ||
      (typeof window !== "undefined" ? localStorage.getItem("email") || "" : ""),
    [profile.personalDetails.email]
  );

  const years = useMemo(() => buildYears(), []);
  const days = useMemo(() => buildDays(), []);

  useEffect(() => {
    if (loading) return;
    const pd = profile.personalDetails;
    setFirstName(pd.firstName || "");
    setLastName(pd.lastName || "");
    setDobDay(pd.dateOfBirth?.day || "");
    setDobMonth(pd.dateOfBirth?.month || "");
    setDobYear(pd.dateOfBirth?.year || "");
    setPhone(pd.phone || "");
  }, [loading, profile.personalDetails]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await updateSection(RENTAL_PROFILE_SECTIONS.PERSONAL_DETAILS, {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dateOfBirth: { day: dobDay, month: dobMonth, year: dobYear },
      phone: phone.trim(),
      email: profileEmail,
    });
    if (ok) navigate("/rental-applications");
  };

  return (
    <Layout1>
      <LayoutContainer className="max-w-xl py-8 md:py-12 lg:py-14">
        <button
          type="button"
          onClick={() => navigate("/rental-applications")}
          className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-[#097FE6] hover:underline"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2} />
          Back
        </button>

        <h1 className="text-2xl font-bold tracking-tight text-[#000929] md:text-3xl">
          Personal details
        </h1>

        <div className="mt-8 rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm md:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="pd-first-name"
                className="mb-1.5 block text-sm font-medium text-[#374151]"
              >
                First name
              </label>
              <input
                id="pd-first-name"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={inputClass}
                autoComplete="given-name"
              />
            </div>

            <div>
              <label
                htmlFor="pd-last-name"
                className="mb-1.5 block text-sm font-medium text-[#374151]"
              >
                Last name
              </label>
              <input
                id="pd-last-name"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={inputClass}
                autoComplete="family-name"
              />
            </div>

            <div>
              <span className="mb-1.5 block text-sm font-medium text-[#374151]">
                Date of birth
              </span>
              <div className="grid grid-cols-3 gap-3">
                <CustomSelect
                  id="pd-dob-day"
                  value={dobDay}
                  onChange={setDobDay}
                  options={days}
                  placeholder="Day"
                  usePortal={false}
                />
                <CustomSelect
                  id="pd-dob-month"
                  value={dobMonth}
                  onChange={setDobMonth}
                  options={MONTHS}
                  placeholder="Month"
                  usePortal={false}
                />
                <CustomSelect
                  id="pd-dob-year"
                  value={dobYear}
                  onChange={setDobYear}
                  options={years}
                  placeholder="Year"
                  usePortal={false}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="pd-phone"
                className="mb-1.5 block text-sm font-medium text-[#374151]"
              >
                Phone number (mobile preferred)
              </label>
              <input
                id="pd-phone"
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                className={inputClass}
                autoComplete="tel"
              />
              <p className="mt-1.5 text-xs leading-relaxed text-[#9CA3AF]">
                Use numbers only, without spaces or other characters, e.g.
                0416222333 or 0244443333.
              </p>
            </div>

            <div>
              <label
                htmlFor="pd-email"
                className="mb-1.5 block text-sm font-medium text-[#374151]"
              >
                Email
              </label>
              <input
                id="pd-email"
                type="email"
                value={profileEmail}
                readOnly
                className={`${inputClass} cursor-default bg-[#F9FAFB] !text-[#9CA3AF]`}
              />
              <p className="mt-1.5 text-xs leading-relaxed text-[#9CA3AF]">
                This email is linked to your profile and can&apos;t be edited
                here.
              </p>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading || saving}
                className="w-full rounded-lg bg-[#097FE6] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#0868c4] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save and back"}
              </button>
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => navigate("/rental-applications")}
                  className="text-sm font-medium text-[#097FE6] hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>

        <button
          type="button"
          onClick={() => setDataUseModalOpen(true)}
          className="mt-10 inline-flex items-center gap-2 text-sm text-[#6B7280] underline decoration-[#6B7280] underline-offset-2 hover:text-[#1A1A1A]"
        >
          <HelpCircle className="h-4 w-4 shrink-0" strokeWidth={2} />
          How this information is used
        </button>
      </LayoutContainer>

      <RentalDataUseModal
        variant="personalDetails"
        open={dataUseModalOpen}
        onClose={() => setDataUseModalOpen(false)}
      />
    </Layout1>
  );
};

export default PersonalDetails;
